import { ChevronDown } from "lucide-react";
import { useId, useMemo, useRef, useState, type FC } from "react";
import { Controller, type FieldValues } from "react-hook-form";
import {
  defaultCountries,
  FlagImage,
  parseCountry,
  usePhoneInput,
  type CountryIso2
} from "react-international-phone";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { FieldShell } from "./FieldShell";
import { useFieldControl, type BaseFieldProps } from "./fieldTypes";

const COUNTRIES = defaultCountries.map(parseCountry);

type Props<T extends FieldValues> = BaseFieldProps<T> & {
  placeholder?: string;
  defaultCountry?: CountryIso2;
};

type ControlProps = {
  id: string;
  value: string;
  onChange: (phone: string) => void;
  onBlur: () => void;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
  defaultCountry: CountryIso2;
  inputRef: (el: HTMLInputElement | null) => void;
};

/** Country picker + masked national number; emits E.164 (`+1555…`). */
const PhoneControl: FC<ControlProps> = ({
  id,
  value,
  onChange,
  onBlur,
  disabled,
  invalid,
  placeholder,
  defaultCountry,
  inputRef
}) => {
  // The hook normalises on mount; only user edits should touch the form.
  const touched = useRef(false);
  const phone = usePhoneInput({
    value,
    defaultCountry,
    disableDialCodeAndPrefix: true,
    disableDialCodePrefill: true,
    onChange: ({ phone: next, inputValue }) => {
      if (!touched.current) return;
      onChange(inputValue.trim() ? next : "");
    }
  });
  const [open, setOpen] = useState(false);
  const listId = `${id}-countries`;
  const dial = phone.country?.dialCode ?? "";

  return (
    <div
      className={cn(
        "flex h-8 w-full min-w-0 items-stretch rounded-lg border border-input transition-colors focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50",
        invalid && "border-destructive ring-3 ring-destructive/20",
        disabled && "pointer-events-none bg-input/50 opacity-50"
      )}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            disabled={disabled}
            aria-label={`Country code +${dial}`}
            aria-expanded={open}
            aria-controls={listId}
            className="h-full shrink-0 gap-1 rounded-r-none rounded-l-[calc(var(--radius-lg)-1px)] border-r border-input px-2 font-normal"
          >
            <FlagImage iso2={phone.country.iso2} className="size-4" />
            <span className="text-sm text-muted-foreground">+{dial}</span>
            <ChevronDown className="size-3.5 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-0">
          <Command>
            <CommandInput placeholder="Search countries…" />
            <CommandList id={listId}>
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {COUNTRIES.map((c) => (
                  <CommandItem
                    key={c.iso2}
                    value={`${c.name} +${c.dialCode}`}
                    data-checked={c.iso2 === phone.country.iso2 || undefined}
                    onSelect={() => {
                      touched.current = true;
                      phone.setCountry(c.iso2, { focusOnInput: true });
                      setOpen(false);
                    }}
                  >
                    <FlagImage iso2={c.iso2} className="size-4" />
                    <span className="truncate">{c.name}</span>
                    <span className="ml-auto text-muted-foreground">
                      +{c.dialCode}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <input
        id={id}
        ref={(el) => {
          phone.inputRef.current = el;
          inputRef(el);
        }}
        type="tel"
        autoComplete="tel-national"
        value={phone.inputValue}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className="h-full min-w-0 flex-1 bg-transparent px-2.5 text-base outline-none placeholder:text-muted-foreground md:text-sm"
        onChange={(e) => {
          touched.current = true;
          phone.handlePhoneValueChange(e);
        }}
        onBlur={onBlur}
      />
    </div>
  );
};

/** International phone input bound to a string field (stored as E.164). */
export function PhoneField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  description,
  disabled,
  className,
  id: idProp,
  placeholder = "Enter phone number",
  defaultCountry = "us"
}: Props<T>) {
  const ctrl = useFieldControl(control);
  const autoId = useId();
  const id = idProp ?? autoId;
  const memoDefault = useMemo(() => defaultCountry, [defaultCountry]);
  return (
    <Controller
      control={ctrl}
      name={name}
      render={({ field, fieldState }) => (
        <FieldShell
          id={id}
          label={label}
          required={required}
          description={description}
          error={fieldState.error}
          disabled={disabled}
          className={className}
        >
          <PhoneControl
            id={id}
            value={String(field.value ?? "")}
            onChange={field.onChange}
            onBlur={field.onBlur}
            disabled={disabled}
            invalid={fieldState.invalid}
            placeholder={placeholder}
            defaultCountry={memoDefault}
            inputRef={field.ref}
          />
        </FieldShell>
      )}
    />
  );
}
